import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { lockInput, unlockInput } from './inputLock.js';
import { audioManager } from '../audio/AudioManager.js';
import { iconText } from './iconText.js';
import { enlargeForTouch } from './touchEnlarge.js';

// The instructor's pre-drive briefing: one line per sign/rule the route raises (from
// lessonScript.mjs's BRIEFING_PL), a single CTA to set off, and ✕/overlay to back out.
// Same shape serves both a lesson (plain items) and the exam (items + an extra `note`
// line for EXAM_RULES_PL) — see DrivingSchoolMenu.

const W = 560;
const ROW_H = 60;
const BODY_TOP = 76;
const NOTE_H = 50;        // one note line…
const NOTE_LINE_H = 22;   // …and each extra one (the exam's price sits under its rules)
const BOTTOM_PAD = 90;   // CTA button + margin

function viewCentre(scene) {
  const cam = scene.cameras.main;
  return { ox: cam.scrollX, oy: cam.scrollY, x: cam.scrollX + GAME_WIDTH / 2, y: cam.scrollY + GAME_HEIGHT / 2 };
}

/**
 * Show the pre-drive briefing. Resolves `true` on the CTA ("Jedziemy!"), `false` on
 * ✕/overlay (cancel — the caller should just re-open the menu, nothing was started yet).
 * `items` = [{icon, name, rule}] from BRIEFING_PL(); `note` (optional) is the exam's
 * EXAM_RULES_PL line plus its price, shown highlighted above the CTA — one `\n`-separated
 * line each, and the card grows to fit them.
 */
export function showLessonBriefing(scene, { title, items = [], cta = 'Jedziemy! {car}', note = null }) {
  return new Promise((resolve) => {
    const { ox, oy, x, y } = viewCentre(scene);
    const noteH = note ? NOTE_H + (String(note).split('\n').length - 1) * NOTE_LINE_H : 0;
    const H = BODY_TOP + items.length * ROW_H + noteH + BOTTOM_PAD;
    const layer = scene.add.container(0, 0).setDepth(2000);
    lockInput(scene);

    const overlay = scene.add.rectangle(ox, oy, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6)
      .setOrigin(0, 0).setInteractive();
    const box = scene.add.rectangle(x, y, W, H, 0xffffff, 1).setStrokeStyle(4, 0x333333);
    const titleText = iconText(scene, x, y - H / 2 + 22, title ?? '', {
      fontFamily: 'system-ui', fontSize: '24px', color: '#000', fontStyle: 'bold',
      align: 'center', wordWrap: { width: W - 80 },
    }).setOrigin(0.5, 0);
    layer.add([overlay, box, titleText]);
    enlargeForTouch(scene, layer, { x, y, w: W, h: H }, { overlay });

    let rowY = y - H / 2 + BODY_TOP;
    for (const item of items) {
      const icon = iconText(scene, x - W / 2 + 34, rowY + ROW_H / 2, item.icon ?? '•', {
        fontFamily: 'system-ui', fontSize: '26px',
      }).setOrigin(0.5);
      const name = iconText(scene, x - W / 2 + 64, rowY + 10, item.name ?? '', {
        fontFamily: 'system-ui', fontSize: '17px', color: '#000', fontStyle: 'bold',
        wordWrap: { width: W - 110 },
      }).setOrigin(0, 0);
      const rule = iconText(scene, x - W / 2 + 64, rowY + 32, item.rule ?? '', {
        fontFamily: 'system-ui', fontSize: '13px', color: '#555',
        wordWrap: { width: W - 110 },
      }).setOrigin(0, 0);
      layer.add([icon, name, rule]);
      rowY += ROW_H;
    }

    if (note) {
      const noteText = iconText(scene, x, rowY + noteH / 2, note, {
        fontFamily: 'system-ui', fontSize: '15px', color: '#a33', fontStyle: 'bold',
        align: 'center', wordWrap: { width: W - 60 },
      }).setOrigin(0.5);
      layer.add(noteText);
    }

    const btnY = y + H / 2 - 40;
    const ctaBtn = scene.add.rectangle(x, btnY, W - 80, 50, 0xffd166)
      .setStrokeStyle(2, 0x333333).setInteractive({ useHandCursor: true });
    const ctaTxt = iconText(scene, x, btnY, cta, {
      fontFamily: 'system-ui', fontSize: '20px', color: '#000', fontStyle: 'bold',
    }).setOrigin(0.5);
    layer.add([ctaBtn, ctaTxt]);

    const closeBtn = scene.add.rectangle(x + W / 2 - 24, y - H / 2 + 24, 36, 36, 0xaa3333, 1)
      .setStrokeStyle(2, 0x000000).setInteractive({ useHandCursor: true });
    const closeTxt = iconText(scene, x + W / 2 - 24, y - H / 2 + 24, '×', {
      fontFamily: 'system-ui', fontSize: '20px', color: '#fff', fontStyle: 'bold',
    }).setOrigin(0.5);
    layer.add([closeBtn, closeTxt]);

    const close = (result) => { audioManager.playSfx('click'); unlockInput(scene); layer.destroy(); resolve(result); };
    ctaBtn.on('pointerup', () => close(true));
    closeBtn.on('pointerup', () => close(false));
    overlay.on('pointerup', () => close(false));
  });
}
